import React, { useCallback, useEffect, useRef, useState } from 'react';
import './Carousel.css';

/**
 * Horizontal carousel: snap scrolling on touch, arrows on pointer devices,
 * optional page dots. Replaces raw overflow-scroll rows so content never
 * looks accidentally cut off.
 */
export default function Carousel({
    children,
    dots = false,
    arrows = true,
    bleed = false,      // run the track edge-to-edge outside the page gutter
    className = '',
    ariaLabel,
}) {
    const trackRef = useRef(null);
    const [nav, setNav] = useState({ page: 0, pages: 1, atStart: true, atEnd: true });

    const measure = useCallback(() => {
        const el = trackRef.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        const pages = max <= 1 ? 1 : Math.ceil(el.scrollWidth / el.clientWidth);
        const page = max <= 1 ? 0 : Math.round((el.scrollLeft / max) * (pages - 1));
        setNav({
            page,
            pages,
            atStart: el.scrollLeft <= 1,
            atEnd: el.scrollLeft >= max - 1,
        });
    }, []);

    useEffect(() => {
        const el = trackRef.current;
        if (!el) return;
        measure();
        el.addEventListener('scroll', measure, { passive: true });
        window.addEventListener('resize', measure);
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (ro) ro.observe(el);
        return () => {
            el.removeEventListener('scroll', measure);
            window.removeEventListener('resize', measure);
            if (ro) ro.disconnect();
        };
    }, [measure, children]);

    const scrollByPage = (dir) => {
        const el = trackRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * el.clientWidth * 0.82, behavior: 'smooth' });
    };

    const goToPage = (i) => {
        const el = trackRef.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        el.scrollTo({ left: (max / Math.max(1, nav.pages - 1)) * i, behavior: 'smooth' });
    };

    const scrollable = nav.pages > 1 || !nav.atEnd || !nav.atStart;

    return (
        <div className={`carousel ${bleed ? 'bleed' : ''} ${className}`}>
            {arrows && scrollable && (
                <button
                    type="button"
                    className="carousel-arrow prev"
                    aria-label="Xem mục trước"
                    disabled={nav.atStart}
                    onClick={() => scrollByPage(-1)}
                >
                    <i className="fa-solid fa-chevron-left" />
                </button>
            )}

            <div className="carousel-track" ref={trackRef} role="group" aria-label={ariaLabel}>
                {children}
            </div>

            {arrows && scrollable && (
                <button
                    type="button"
                    className="carousel-arrow next"
                    aria-label="Xem mục tiếp theo"
                    disabled={nav.atEnd}
                    onClick={() => scrollByPage(1)}
                >
                    <i className="fa-solid fa-chevron-right" />
                </button>
            )}

            {dots && nav.pages > 1 && (
                <div className="carousel-dots">
                    {Array.from({ length: nav.pages }).map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            className={`carousel-dot ${i === nav.page ? 'on' : ''}`}
                            aria-label={`Trang ${i + 1}`}
                            onClick={() => goToPage(i)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
